/**
 * Narrowing the question list to one response result.
 *
 * The breakdown counts sit above the list, and selecting one of them asks "which questions
 * are these?". This decides which questions answer that.
 */
import { ResponseResult } from '@amzn/gbsa-helios-typescript-client';

import type { InsightQuestion } from './IncompleteAuditInsights';

/**
 * Which count is selected.
 *
 * A const map rather than a bare union so the ids cannot drift between the type and the
 * code comparing against them.
 */
export const QUESTION_FILTER = {
  total: 'total',
  pass: 'pass',
  fail: 'fail',
  na: 'na',
} as const;

export type QuestionFilter = (typeof QUESTION_FILTER)[keyof typeof QUESTION_FILTER];

/**
 * The result each filter keeps.
 *
 * `total` is absent on purpose: it keeps every question, including one still unanswered,
 * which no result describes.
 */
const FILTER_RESULT = new Map<QuestionFilter, ResponseResult>([
  [QUESTION_FILTER.pass, ResponseResult.PASS],
  [QUESTION_FILTER.fail, ResponseResult.FAIL],
  [QUESTION_FILTER.na, ResponseResult.NA],
]);

/** What the question list shows for the selected count. */
export function filterQuestions(
  questions: readonly InsightQuestion[],
  filter: QuestionFilter,
): InsightQuestion[] {
  const result = FILTER_RESULT.get(filter);
  if (!result) {
    return [...questions];
  }

  return questions.filter((question) => question.result === result);
}

/** How many questions are shown and what they are worth. */
export function questionTally(questions: readonly InsightQuestion[]): {
  count: number;
  points: number;
} {
  return {
    count: questions.length,
    points: questions.reduce((sum, question) => sum + question.points, 0),
  };
}
